/**
 * Cloud Storage Service
 * Syncs games to backend API when user is logged in
 * Falls back to localStorage when not authenticated
 */

import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Configure axios for credentials
axios.defaults.withCredentials = true;

/**
 * Check if user is authenticated by calling /api/auth/me
 */
async function isAuthenticated() {
  try {
    const { data } = await axios.get(`${API_URL}/api/auth/me`, {
      withCredentials: true
    });
    return !!data?.id;
  } catch {
    return false;
  }
}

/**
 * Save a game to the cloud (or localStorage if not authenticated)
 */
export async function saveGameCloud(name, gameData) {
  const authenticated = await isAuthenticated();
  
  if (authenticated) {
    try {
      const { data } = await axios.post(`${API_URL}/api/games`, {
        name,
        fen: gameData.fen || gameData.position,
        pgn: gameData.pgn || '',
        move_history: gameData.history || gameData.moveHistory || [],
        coach_mode: gameData.coachEnabled ?? true
      }, { withCredentials: true });
      
      return {
        ...data,
        savedAt: data.created_at || data.updated_at,
        isCloud: true
      };
    } catch (error) {
      console.error('Cloud save failed, falling back to localStorage:', error);
      // Fall through to localStorage
    }
  }
  
  // Fallback to localStorage
  const { saveGame } = await import('./storage');
  const localGame = saveGame(name, gameData);
  return localGame ? { ...localGame, isCloud: false } : null;
}

/**
 * Get all saved games (from cloud if authenticated, else localStorage)
 */
export async function getSavedGamesCloud() {
  const authenticated = await isAuthenticated();
  
  if (authenticated) {
    try {
      const { data } = await axios.get(`${API_URL}/api/games`, {
        withCredentials: true
      });
      
      // Transform to match local storage format
      return data.map(game => ({
        id: game.id,
        name: game.name,
        fen: game.fen,
        position: game.fen,
        pgn: game.pgn,
        history: game.move_history,
        moveHistory: game.move_history,
        coachEnabled: game.coach_mode,
        savedAt: game.updated_at || game.created_at,
        isCloud: true
      }));
    } catch (error) {
      console.error('Cloud fetch failed, falling back to localStorage:', error);
    }
  }
  
  // Fallback to localStorage
  const { getSavedGames } = await import('./storage');
  const localGames = getSavedGames();
  return localGames.map(g => ({ ...g, isCloud: false }));
}

/**
 * Load a specific saved game
 */
export async function loadSavedGameCloud(id) {
  const authenticated = await isAuthenticated();
  
  if (authenticated) {
    try {
      const { data } = await axios.get(`${API_URL}/api/games/${id}`, {
        withCredentials: true
      });
      
      return {
        id: data.id,
        name: data.name,
        fen: data.fen,
        position: data.fen,
        pgn: data.pgn,
        history: data.move_history,
        moveHistory: data.move_history,
        coachEnabled: data.coach_mode,
        savedAt: data.updated_at || data.created_at,
        isCloud: true
      };
    } catch (error) {
      console.error('Cloud load failed:', error);
    }
  }
  
  // Fallback to localStorage
  const { loadSavedGame } = await import('./storage');
  const localGame = loadSavedGame(id);
  return localGame ? { ...localGame, isCloud: false } : null;
}

/**
 * Delete a saved game
 */
export async function deleteSavedGameCloud(id) {
  const authenticated = await isAuthenticated();
  
  if (authenticated) {
    try {
      await axios.delete(`${API_URL}/api/games/${id}`, {
        withCredentials: true
      });
      return true;
    } catch (error) {
      console.error('Cloud delete failed:', error);
      // Still try to delete from localStorage
    }
  }
  
  // Also delete from localStorage (in case it exists there)
  const { deleteSavedGame } = await import('./storage');
  return deleteSavedGame(id);
}

/**
 * Update an existing game
 */
export async function updateSavedGameCloud(id, gameData) {
  const authenticated = await isAuthenticated();
  
  if (authenticated) {
    try {
      const { data } = await axios.put(`${API_URL}/api/games/${id}`, {
        name: gameData.name,
        fen: gameData.fen || gameData.position,
        pgn: gameData.pgn,
        move_history: gameData.history || gameData.moveHistory,
        coach_mode: gameData.coachEnabled
      }, { withCredentials: true });
      
      return {
        ...data,
        isCloud: true
      };
    } catch (error) {
      console.error('Cloud update failed:', error);
      return null;
    }
  }
  
  // localStorage doesn't have update - just return null
  return null;
}

/**
 * Sync local games to cloud after login
 */
export async function syncLocalGamesToCloud() {
  const authenticated = await isAuthenticated();
  if (!authenticated) return { synced: 0 };
  
  try {
    const { getSavedGames } = await import('./storage');
    const localGames = getSavedGames();
    
    if (localGames.length === 0) return { synced: 0 };
    
    let synced = 0;
    for (const game of localGames) {
      try {
        await axios.post(`${API_URL}/api/games`, {
          name: game.name,
          fen: game.fen || game.position,
          pgn: game.pgn || '',
          move_history: game.history || game.moveHistory || [],
          coach_mode: game.coachEnabled ?? true
        }, { withCredentials: true });
        synced++;
      } catch (error) {
        console.error(`Failed to sync game ${game.name}:`, error);
      }
    }
    
    // Clear localStorage games after successful sync
    if (synced > 0) {
      localStorage.removeItem('boosty_chess_saved');
    }
    
    return { synced, total: localGames.length };
  } catch (error) {
    console.error('Sync failed:', error);
    return { synced: 0, error: error.message };
  }
}
